import { HttpClient } from "@angular/common/http";
import { Component, Input } from "@angular/core";
import { UserPermissionType } from "@apiserver-rest/ponder-auth-client-base";
import { PonderDataClient } from "@apiserver-rest/ponder-data-client";
import { GetPresignedUrlResponse } from "@apiserver-rest/ponder-data-client-base";
import { AuthSession } from "@ponder-core/auth/auth-session";
import { AuthService } from "@ponder-core/auth/auth.service";
import { RoleAndPermissions } from "@ponder-core/auth/role-permissions";
import { EventBroker } from "@ponder-core/events/eventBroker";
import { getProfileImageRoute } from "@ponder-features/user/profile/profile-image-router";
import { BusySpinComponent } from "@ponder-shared/components/busy-spin.component";
import { Subscription, take } from "rxjs";

@Component({
    selector: "user-avatar-change",
    imports: [BusySpinComponent],
    templateUrl: "./user-account-avatar-change.component.html"
})
export class UserAccountAvatarChangeComponent {
    @Input({ required: true }) public authSession?: AuthSession;

    public avatarUrl!: string;
    public canUploadCustomImage: boolean = false;
    public isUploading: boolean = false;
    public uploadError: boolean = false;
    private avatarUrlUpdatedSub!: Subscription;

    public validExtensions: string = ".png,.jpg,.jpeg,.gif,.bmp";
    public fileSizeError: boolean = false;
    public fileExtensionError: boolean = false;

    private file?: File;

    public constructor(
        private events: EventBroker, 
        private authService: AuthService, 
        private ponderDataClient: PonderDataClient,
        private httpClient: HttpClient) {
        
        this.avatarUrlUpdatedSub = this.events.profile.profileAvatarUpdatedEvent.subscribe({
            next: () => {
                this.avatarUrl = getProfileImageRoute(this.authSession!.username!);
            }
        });
    }

    public ngOnDestroy(): void {
        this.avatarUrlUpdatedSub.unsubscribe();
    }

    public ngOnInit(): void {
        this.avatarUrl = getProfileImageRoute(this.authSession!.username!);

        this.authService.getRoleAndPermissions().then((roleAndPermissions?: RoleAndPermissions) => {
            if (roleAndPermissions && roleAndPermissions.hasPermission(UserPermissionType.CustomProfilePicture)) {
                this.canUploadCustomImage = true;
            }
        });
    }
    
    public onFileSelected(fileInput: HTMLInputElement): void {
        this.file = undefined;
        this.fileSizeError = false;
        this.fileExtensionError = false;

        if (fileInput.files?.length) {
            this.file = fileInput.files[0];

            this.validateFileSize();
            this.validateFileExtension();

            if (!this.fileSizeError && !this.fileExtensionError) {
                this.onUploadProfileImage();
            } else {
                this.file = undefined;
            }
        }
    }

    private validateFileSize(): void {
        if (this.file!.size! > 1000000) {
            this.fileSizeError = true;
        }
    }

    private validateFileExtension(): void {
        var nameLower = this.file!.name.toLowerCase();
        var extension = nameLower.substring(nameLower.lastIndexOf("."));
        var validExtensions = this.validExtensions.split(',');

        this.fileExtensionError = true;

        validExtensions.forEach(e => {
            if (e == "*" || extension == e.toLowerCase()) {
                this.fileExtensionError = false;
            }
        });
    }

    private onUploadProfileImage(): void {
        if (!this.canUploadCustomImage || !this.file || this.isUploading) {
            return;
        }

        this.isUploading = true;
        this.uploadError = false;

        const contentType = this.getContentType();

        if (!contentType) {
            this.isUploading = false;
            this.uploadError = true;
            return;
        }

        this.ponderDataClient
            .artifactGeneratePresignedUrlProfileImage(contentType, this.file.size)
            .pipe(take(1))
            .subscribe({
                next: (response: GetPresignedUrlResponse) => {
                    if (response.success && response.presignedUrl) {
                        this.httpClient
                            .put(response.presignedUrl, this.file!, {
                                headers: {
                                    "Content-Type": contentType,
                                    "Content-Length": this.file!.size + "",
                                    "x-amz-acl": "public-read"
                                }
                            })
                            .pipe(take(1))
                            .subscribe({
                                next: () => {
                                    this.events.profile.profileAvatarUpdatedEvent.emit();
                                },
                                error: () => {
                                    this.uploadError = true;
                                }
                            })
                            .add(() => { this.isUploading = false; });
                    } else {
                        this.uploadError = true;
                        this.isUploading = false;
                    }
                },
                error: () => {
                    this.uploadError = true;
                    this.isUploading = false;
                }
            });
    }

    private getContentType(): string | undefined {
        if (!this.file) {
            return undefined;
        }

        const extension = this.file.name.split('.').pop();

        switch (extension) {
            case "png":
                return "image/png";

            case "jpg":
            case "jpeg":
                return "image/jpeg";

            case "gif":
                return "image/gif";

            case "bmp":
                return "image/bmp";

            default:
                return undefined;
        }
    }
}